/**
 * @author Vibhor<vibhor.sharma@314ecorp.com>
 * @description React Hook useUpdateManualWorklistEntity
 */
import _ from 'lodash';
import { JsonTree } from '@react-awesome-query-builder/antd';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';

import api from 'client/index';
import { getWorklistRule } from '../useAllClaimStatusRequests';
import AppEvents from '@veritable/common/utils/appEvents';
import { insertRuleIntoGroup } from 'utils/queryBuilder';
import { buildCriteria } from 'utils/advancedFilterUtils';
import { getClaimAdfFieldMap } from 'utils/adfUtils';
import QUERY_KEYS from 'app-constants/queryKeys';
import { useCustomAttributesStoreValues } from 'store/customAttributes';
import { CUSTOM_ATTRIBUTE_ENTITY_TYPE } from 'app-constants/admin/customAttribute';

/**
 * Mutate for update manual worklist entity
 *
 */
interface IProps {
	entityType: ClaimWorkLists.IWorkListEntityType;
	multipleWorklistPatch: ClaimWorkLists.IMultipleWorklistPatch;
	claimStateGroup?: ClaimRequest.IGroupedClaimWorkState;
	criteria: JsonTree;
}
const useUpdateManualWorklistEntity = (): UseMutationResult<unknown, IAxiosError, IProps, unknown> => {
	const queryClient = useQueryClient();
	const { customAttributes } = useCustomAttributesStoreValues();
	const claimAttributes = customAttributes?.[CUSTOM_ATTRIBUTE_ENTITY_TYPE.claim];
	return useMutation({
		mutationFn: async ({ entityType, multipleWorklistPatch, claimStateGroup, criteria }) => {
			let criteriaRule = criteria;
			if (claimStateGroup) {
				const worklistRule = getWorklistRule(claimStateGroup);
				criteriaRule = insertRuleIntoGroup(criteriaRule, worklistRule);
			}
			const splittedCriteria = buildCriteria(criteriaRule, getClaimAdfFieldMap(claimAttributes));
			if (!_.isEmpty(multipleWorklistPatch?.removeClaims)) {
				multipleWorklistPatch.removeClaims = {
					conjunction: 'AND',
					filters: [splittedCriteria],
				};
			} else if (!_.isEmpty(multipleWorklistPatch?.addClaims)) {
				multipleWorklistPatch.addClaims = {
					conjunction: 'AND',
					filters: [splittedCriteria],
				};
			}
			await api.WorklistApi.updateManualWorklistEntities({
				entityType,
				multipleWorklistPatch,
			});
		},
		onSuccess: () => {
			AppEvents.publishApiSuccess();
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.claimWorkLists] });
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.claimWorkListById] });
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.claimRequestById] });
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.claimStatusRequests] });
		},
		onError: (error) => {
			AppEvents.publishApiError({ error });
		},
	});
};

export default useUpdateManualWorklistEntity;
